import React from 'react';
import PropTypes from 'prop-types';
import { Column, Typography } from '@tempmee/react';
import { useMemberApplication } from '../../../../domain/Awards';
import { MemberAnswerList } from './MemberAnswerList';
import { MemberContentDialog, memberShape } from './MemberContentDialog';

/**
 * What one member submitted, opened from the Application tick on the roster.
 *
 * "No application" is a normal state rather than a failure: an admin can set
 * someone to applicant without an application ever existing, and the service
 * resolves that 404 to null. So a null lands on the shell's empty branch, and
 * only `isError` — a 403 or a 500 — reads as something going wrong.
 */
export const MemberApplicationDialog = ({ open, onClose, cycleId, member }) => {
  const { application, isPending, isError } = useMemberApplication(cycleId, member.userId);

  return (
    <MemberContentDialog
      open={open}
      onClose={onClose}
      title={`Application — ${member.displayName}`}
      isPending={isPending}
      isError={isError}
      isEmpty={!application}
      loadingMessage="Loading application…"
      errorMessage="Could not load this application."
      emptyMessage="This member has not submitted an application in this cycle."
    >
      {application ? (
        <Column gap={5}>
          <MemberAnswerList answers={application.answers} applicantName={member.name} />

          <Column gap={1}>
            <Typography variant="body2" bold className="text-text-secondary">
              Mailing address
            </Typography>
            <Typography variant="body1">{application.mailingAddress}</Typography>
          </Column>

          <Column gap={1}>
            <Typography variant="body2" bold className="text-text-secondary">
              Submitted
            </Typography>
            <Typography variant="body1">{application.submittedAtLabel}</Typography>
          </Column>
        </Column>
      ) : null}
    </MemberContentDialog>
  );
};

MemberApplicationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  cycleId: PropTypes.string.isRequired,
  member: memberShape.isRequired,
};
